import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { MemorialService, PropostaService } from 'administrativo-lib';
import { Favorecido, FuncaoService, GlobalService, Licitacao, Login, Memorial, Proponente, Proposta, SituacaoProposta } from 'eddydata-lib';
import { Workbook, Worksheet } from 'exceljs';
import { ConfirmationService } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as toastr from 'toastr';

declare var $: any;

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-proposta',
  templateUrl: './proposta.component.html',
  styleUrls: ['./proposta.component.css']
})
export class PropostaComponent implements OnInit, OnDestroy, OnChanges {

  private indexInicial = 5;
  public itemAtual: Proposta;
  public proponenteAtual: Proponente;
  public apenasCotacoes: boolean = false;
  public marcas: string[];
  public erros: string[];
  public visualizarProponentes = false;
  private situacoesProposta: { id: SituacaoProposta, nome: string, style: string, ordem: number }[];

  public imaskConfig = {
    mask: Number,
    scale: 4,
    signed: true,
    thousandsSeparator: '.',
    padFractionalZeros: true,
    normalizeZeros: true,
    radix: ','
  };

  @Input() edicao: boolean = true; // variavel para definir se opções de edições estarão disponiveis
  @Input() entidade: Licitacao;
  @Input() login: Login;
  @Input() dataAtualizacao: Date;
  @Output() julgamentoEvent: EventEmitter<any> = new EventEmitter();

  protected unsubscribe: Subject<void> = new Subject();

  constructor(
    protected confirmationService: ConfirmationService,
    public funcaoService: FuncaoService,
    protected globalService: GlobalService,
    protected memorialService: MemorialService,
    protected propostaService: PropostaService,
  ) { }

  ngOnInit() {
    this.situacoesProposta = this.globalService.obterSituacoesProposta();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entidade'] || changes['dataAtualizacao']) {
      setTimeout(() => this.converterPropostas(), 1000);
    }
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  compareFn(c1: any, c2: any): boolean {
    if (c1 && c2) {
      if (c1.id && c2.id) {
        return c1.id === c2.id;
      } else if (c1.chave && c2.chave) {
        return c1.chave == c2.chave;
      } else {
        return c1 === c2;
      }
    } else {
      return false;
    }
  }

  public carregarPropostas() {
    if (this.proponenteAtual) {
      if (!this.proponenteAtual.propostas) {
        this.proponenteAtual.propostas = [];
      }
      if (!this.apenasCotacoes) {
        this.entidade.itens.forEach((item) => {
          if (!this.proponenteAtual.propostas.find((p) => p.memorial.id === item.id)) {
            const prop = new Proposta();
            prop.situacao = 'NAO_JULGADO';
            prop.memorial = Object.assign({}, item)
            delete prop.memorial.propostas;

            this.proponenteAtual.propostas.push(prop);
          }
        });
      }
      this.proponenteAtual.propostas.sort((a, b) => { return a.memorial.ordem - b.memorial.ordem });
      this.atualizarMarcas()
      this.converterPropostas();
    }
  }

  public atualizarMarcas() {
    this.marcas = [];
    if (this.entidade?.proponentes) {
      for (let i = 0; i < this.entidade.proponentes.length; i++) {
        const proponente = this.entidade.proponentes[i];
        if (proponente.propostas) {
          this.marcas = this.marcas.concat(proponente.propostas.filter(p => p.marca !== undefined).map((p) => String(p.marca).trim()));
        }
      }
      // remove duplicados
      this.marcas = [...new Set(this.marcas)];
      // remove undefined e null
      if (this.marcas.includes(undefined)) this.marcas.splice(this.marcas.indexOf(undefined), 1);
      if (this.marcas.includes(null)) this.marcas.splice(this.marcas.indexOf(null), 1);
    }
  }

  public confirmar() {
    this.carregarPropostas();
    this.removerItensVazios();
    this.proponenteAtual = undefined;
    $('#dialogPropostaCad').modal('hide');
    toastr.info('Propostas inseridas, para persistir as alterações salve o registro ao concluir!');
  }

  public removerItensVazios() {
    if (this.entidade?.proponentes) {
      for (let i = 0; i < this.entidade.proponentes.length; i++) {
        const proponente = this.entidade.proponentes[i];

        if (proponente.propostas) {
          for (let j = 0; j < proponente.propostas.length; j++) {
            const proposta = proponente.propostas[j];

            if (!proposta.valor_unitario || +proposta.valor_unitario <= 0) {
              if (proposta.id) {
                this.propostaService.remover(proposta.id).pipe(takeUntil(this.unsubscribe))
                  .subscribe((res) => {
                    proponente.propostas.splice(j, 1);
                    j--;
                  }, (err) => toastr.error(err.error.payload));
              } else {
                proponente.propostas.splice(j, 1);
                j--;
              }
            }

          }
        }

      }
    }
  }

  public toggleApenasCotacoes() {
    if (this.apenasCotacoes) {
      this.removerItensVazios();
    } else {
      this.carregarPropostas();
    }
  }

  public async onUpload(event: any, fileUpload: any) {
    if (!this.proponenteAtual) {
      toastr.warning('Nenhum favorecido selecionado para importação!')
      return;
    }
    if (!event.files || event.files.length === 0) {
      toastr.warning('Nenhum arquivo selecionado!')
      return;
    }

    try {
      // variavel para guardar informacoes do mapa de precos
      const regXlsx: { ordem: number, marca: string, valor: number }[] = [];
      this.erros = [];

      // le mapa de preços enviado (xlsx)
      const file: File = event.files[0];
      const wb = new Workbook();
      await wb.xlsx.load(await file.arrayBuffer());
      const ws: Worksheet = wb.getWorksheet('Memorial');

      const idXlsx = ws.getCell('C1').value ? +String(ws.getCell('C1').value) : undefined;
      if (!idXlsx || idXlsx !== this.entidade.id) {
        this.erros.push(`Código inválido no arquivo de mapa de preços, verifique se o arquivo pertence a outro memorial`);
      } else {
        let cnpjXlsx = ws.getCell('A3').value ? this.funcaoService.removerPontos(String(ws.getCell('A3').value)).trim() : undefined;
        if (!cnpjXlsx || cnpjXlsx === 'Informe o Documento') {
          this.erros.push(`Documento não informado no arquivo`);
        } else {
          if (cnpjXlsx.length > 11) cnpjXlsx = this.funcaoService.strZero(cnpjXlsx, 14);
          if (this.funcaoService.removerPontos(String(this.proponenteAtual.favorecido.cpf_cnpj)).trim() !== cnpjXlsx) {
            this.erros.push(`Documento informado no arquivo (${cnpjXlsx}) é diferente do proponente atual`);
          }
        }

        for (let linha = this.indexInicial; linha < this.entidade.itens.length + this.indexInicial; linha++) {
          const itemMemorial: Memorial = this.entidade.itens[linha - this.indexInicial];
          const itemXlsx = {
            ordem: +ws.getCell(`A${linha}`).value?.toString(),
            marca: ws.getCell(`E${linha}`).value?.toString(),
            valor: +ws.getCell(`F${linha}`).value?.toString()
          };

          // verifica a integridade do arquivo
          if (itemMemorial.ordem !== itemXlsx.ordem) {
            this.erros.push(`Célula A${linha} com valor inválido (esperado ${itemMemorial.ordem})`);
          }
          if (+itemXlsx.valor < 0) {
            this.erros.push(`Célula F${linha} com valor inválido (esperado valor positivo)`);
          }

          regXlsx.push(itemXlsx);
        }

        if (!regXlsx.find(x => +x.valor > 0)) {
          this.erros.push(`Nenhum item foi cotado no arquivo informado!`);
        }
      }

      if (this.erros.length > 0) {
        throw new Error(`${this.erros.length} erro(s) encontrado(s) no preenchimento do mapa de preços, verifique o conteúdo do arquivo e importe novamente!`);
      }

      await this.importarMapaPrecos(regXlsx);

      toastr.success('Mapa de preço importado com sucesso!');
      $('#dialogImportarMapaPreco').modal('hide');
      $('#dialogPropostaCad').modal('show');
    } catch (ex) {
      toastr.error(ex.message ? ex.message : ex);
    } finally {
      fileUpload.clear();
    }

  }

  public fecharImportar() {
    this.erros = [];
    $('#dialogImportarMapaPreco').modal('hide');
    $('#dialogPropostaCad').modal('show');
  }

  private async importarMapaPrecos(registros: { ordem: number, marca: string, valor: number }[]) {

    this.carregarPropostas();

    for (let i = 0; i < this.entidade.itens.length; i++) {
      const itemProposta = this.proponenteAtual.propostas[i];
      const itemXlsx = registros[i];

      if (itemProposta.memorial.ordem !== itemXlsx.ordem) {
        throw new Error(`Ordem inválida para o item ${itemProposta.memorial.ordem}, valor preenchido é ${itemXlsx.ordem}`)
      }

      itemProposta.marca = itemXlsx.marca;
      itemProposta.valor_unitario = itemXlsx.valor;
    }

    this.removerItensVazios();

  }

  private converterPropostas() {
    if (this.entidade?.itens) {
      this.entidade.itens.forEach((memorial) => {
        let propostas: Proposta[] = [];

        if (this.entidade?.proponentes) {
          this.entidade.proponentes.forEach((proponente) => {
            if (proponente.propostas) {
              proponente.propostas.filter((p) => { return p.memorial.ordem === memorial.ordem && +p.valor_unitario > 0 })
                .forEach(p => {
                  const prop = Object.assign({}, p);

                  // prop.proponente = Object.assign({}, proponente);
                  // delete prop.proponente.propostas;

                  /*
                  prop.proponente = new Proponente();
                  prop.proponente.id = proponente.id;
                  prop.proponente.epp_me = proponente.epp_me;
                  prop.proponente.favorecido = proponente.favorecido;
                  prop.proponente.motivo = proponente.motivo;
                  prop.proponente.ordem = proponente.ordem;
                  prop.proponente.situacao = proponente.situacao;
                  */

                  prop.proponente = proponente;
                  propostas.push(prop);
                })
            }
          })
        }

        memorial.propostas = propostas;
        this.ordenarPropostas(memorial.propostas)
      })
    }
  }

  private async ordenarPropostas(propostas: Proposta[]) {
    // ordena propostas pela ordenação da situação e por valor, respectivamente
    propostas
      .sort((a, b) => { return a.valor_unitario - b.valor_unitario })
      .sort((a, b) => { return this.situacoesProposta.find(s => s.id === a.situacao).ordem - this.situacoesProposta.find(s => s.id === b.situacao).ordem });
  }

  public julgarPropostas() {
    this.julgamentoEvent.emit();
  }

  public obterNomeProposta(item: Proposta): string {
    try {
      return this.situacoesProposta.find(p => p.id === item.situacao).nome;
    } catch (err) {
      return item.situacao;
    }
  }

  public obterEstiloProposta(item: Proposta): string {
    try {
      return this.situacoesProposta.find(p => p.id === item.situacao).style;
    } catch (err) {
      return 'secondary';
    }
  }
}
